import type { ListViewConfig } from './list-view';
import type { FilterRow } from './query';
import { dateSort } from './tracking-config';

export type AdminPage =
  | 'risks'
  | 'logs'
  | 'templates'
  | 'mapping-fields'
  | 'courses'
  | 'attachments'
  | 'approval-fields'
  | 'template-rules'
  | 'hrm-fields'
  | 'deliveries'
  | 'education'
  | 'experience'
  | 'contacts'
  | 'batch';
const field = (key: string, label: string) => ({ key, label });
export function adminConfig(page: AdminPage, scope = ''): ListViewConfig {
  const facets =
    page === 'risks'
      ? [field('status', '处理状态'), field('type', '异常类型')]
      : page === 'logs'
        ? [field('actor', '操作人'), field('action', '操作类型'), field('target', '对象类型')]
        : page === 'attachments'
          ? [field('type', '文件类型')]
          : [
                'courses',
                'mapping-fields',
                'hrm-fields',
                'approval-fields',
                'template-rules',
                'deliveries',
                'batch'
              ].includes(page)
            ? [field('status', '状态')]
            : [];
  const dates = ['risks', 'logs', 'deliveries'].includes(page)
    ? [field('created', '创建时间'), ...(page === 'risks' ? [field('updated', '更新时间')] : [])]
    : [];
  return {
    key: `admin-${page}-${scope}`,
    facets,
    dates,
    sorts: [
      field('order', '原有顺序'),
      field('name', '名称'),
      ...(page === 'risks' ? [field('priority', '待处理优先')] : []),
      ...dates
    ],
    groups: facets,
    defaults: {
      keyword: '',
      selected: {},
      date: { field: dates[0]?.key ?? '', mode: 'ALL' },
      sort: {
        field: page === 'risks' ? 'priority' : dates.length ? 'created' : 'order',
        direction: dates.length && page !== 'risks' ? 'desc' : 'asc'
      },
      groupBy: ''
    }
  };
}
export const mappingStatusLabels: Record<string, string> = {
  MAPPED: '直接映射',
  DERIVED: '规则计算',
  FORMULA_FILL_DOWN: '公式下拉',
  INTENTIONALLY_BLANK: '确认留空',
  PENDING_CONFIRMATION: '待确认'
};
const searchFields: Record<AdminPage, string[]> = {
  risks: ['message', 'targetId'],
  logs: ['actor', 'action', 'targetType', 'targetId', 'changeReason'],
  templates: ['name'],
  'mapping-fields': ['targetHeader', 'sourceLabel', 'sourcePath', 'transform'],
  courses: ['title'],
  attachments: ['fileName', 'attachmentType'],
  'approval-fields': ['approvalFieldLabel', 'targetField'],
  'template-rules': ['name', 'templateId', 'employeeTypes', 'departments'],
  'hrm-fields': ['name', 'sourcePath', 'groupName', 'fieldCode'],
  deliveries: ['taskId', 'audience', 'channels'],
  education: ['school_name', 'school', 'schoolName', 'major', 'degree', 'education_level'],
  experience: ['employer', 'company', 'companyName', 'position', 'department'],
  contacts: ['name', 'relationship'],
  batch: ['employeeName', 'employeeType']
};
export function projectAdmin(page: AdminPage, row: Record<string, unknown>, index: number): FilterRow {
  const value = (key: string) =>
    row[key] === undefined || row[key] === null
      ? undefined
      : Array.isArray(row[key])
        ? row[key].join('、')
        : typeof row[key] === 'object'
          ? undefined
          : String(row[key]);
  const name = searchFields[page].map(value).filter(Boolean).join(' ');
  let status = value('status');
  if (page === 'risks') status = status === 'RESOLVED' ? '已解决' : status === 'PENDING' ? '待处理' : '待核实';
  if (page === 'courses') status = row.published ? '已发布' : row.uploaded ? '待预览发布' : '待上传视频';
  if (page === 'mapping-fields') status = mappingStatusLabels[status ?? ''] ?? '待确认';
  if (page === 'hrm-fields')
    status =
      row.enabled === false
        ? '停用'
        : row.syncStatus === 'ATTACHMENT_PENDING'
          ? '附件可同步'
          : !row.syncStatus || row.syncStatus === 'READY'
            ? '可同步'
            : '缺少元数据';
  if (page === 'approval-fields' || page === 'template-rules') status = row.enabled ? '启用' : '停用';
  const dates = { created: value('createdAt'), updated: value('updatedAt') };
  const id =
    page === 'mapping-fields'
      ? `${value('targetHeader')}:${value('sourcePath')}`
      : (value('id') ?? value('materialId') ?? `${page}:${index}`);
  return {
    id,
    search: name,
    facets: {
      status,
      type: value(page === 'attachments' ? 'mimeType' : 'type'),
      actor: value('actor'),
      action: value('action'),
      target: value('targetType')
    },
    dates,
    sort: {
      ...dateSort(dates),
      name,
      order: index,
      priority: (row.status === 'RESOLVED' ? 1e15 : 0) - (dateSort(dates).created ?? -8.64e13)
    }
  };
}
